/**
 * @file Manages the "attract mode" or "demo mode" of the game.
 * @module DemoManager
 */

import { demoScripts } from '../config/demoScripts.js';

export class DemoManager {
    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {import('../Game.js').Game} dependencies.game - The main game instance.
     * @param {function} dependencies.startDemoGameCallback - Callback to start a new game in demo mode.
     * @param {function} dependencies.endDemoGameCallback - Callback to end the demo and return to the intro.
     */
    constructor({ game, startDemoGameCallback, endDemoGameCallback }) {
        this.game = game;
        this.startDemoGameCallback = startDemoGameCallback;
        this.endDemoGameCallback = endDemoGameCallback;

        this.idleTimer = null;
        this.actionTimeout = null;
        this.isDemoRunning = false;
        this.currentDemoConfig = null;
        this.currentActionIndex = 0;

        this.IDLE_TIMEOUT = 10000; // 60 seconds
        this.ACTION_DELAY = 1500; // 1.5 seconds between clicks
    }

    /**
     * Starts the inactivity timer. If it completes, the demo mode is triggered.
     */
    startIdleTimer() {
        this.resetIdleTimer();
        console.log('[DemoManager] Idle timer started.');
        this.idleTimer = setTimeout(() => {
            console.log('[DemoManager] Idle timer expired. Starting demo.');
            this.isDemoRunning = true;
            this.startDemoGameCallback();
        }, this.IDLE_TIMEOUT);
    }

    /**
     * Resets the inactivity timer. Called on any user interaction.
     */
    resetIdleTimer() {
        if (this.idleTimer) {
            clearTimeout(this.idleTimer);
            this.idleTimer = null;
        }
    }

    /**
     * Stops the demo, cleans up, and calls the callback to return to the intro screen.
     */
    stopDemo() {
        if (!this.isDemoRunning) return;

        console.log('[DemoManager] Demo stopped by user interaction.');
        this.isDemoRunning = false;
        if (this.actionTimeout) {
            clearTimeout(this.actionTimeout);
            this.actionTimeout = null;
        }
        this.endDemoGameCallback();
    }

    /**
     * Starts executing a demo script.
     */
    run() {
        if (demoScripts.length === 0) {
            console.warn('[DemoManager] No demo scripts available.');
            this.stopDemo();
            return;
        }

        this.isDemoRunning = true;
        this.currentActionIndex = 0;

        console.log('[DemoManager] Running demo script.');
        this._executeNextAction();
    }

    getRandomDemoConfig() {
        return demoScripts[Math.floor(Math.random() * demoScripts.length)];
    }

    setCurrentDemoConfig(config) {
        this.currentDemoConfig = config;
    }

    /**
     * Executes the next action in the current script.
     * @private
     */
    _executeNextAction() {
        if (!this.isDemoRunning || !this.currentDemoConfig || this.currentActionIndex >= this.currentDemoConfig.script.length || this.game.gameState.isGameOver) {
            console.log('[DemoManager] Demo script finished or game over.');
            this.stopDemo();
            return;
        }

        const action = this.currentDemoConfig.script[this.currentActionIndex];
        
        // Use a timeout to simulate a player's thinking time
        this.actionTimeout = setTimeout(() => {
            if (!this.isDemoRunning) return; // Check again in case demo was stopped during timeout

            if (this.game.gameController) {
                console.log(`[DemoManager] Executing action: click at (${action.r}, ${action.c})`);
                this.game.gameController.performProgrammaticClick(action.r, action.c);
            }

            this.currentActionIndex++;
            this._executeNextAction();

        }, this.ACTION_DELAY);
    }
}